import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LakehousesPage.css';

const problems = [
  {
    title: 'Numbers disagree',
    text: 'The same measure gives different answers depending on who built the report.',
  },
  {
    title: 'Reporting is manual',
    text: 'Analysts rebuild extracts by hand every period, so insight arrives late.',
  },
  {
    title: 'Nothing to build on',
    text: 'Each new report starts from scratch because there is no shared, governed data foundation.',
  },
];

const stats = [
  { value: '90', label: 'days from kickoff to handover' },
  { value: '10', label: 'source tables in one medallion pipeline' },
  { value: '1', label: 'agreed reporting outcome delivered' },
  { value: 'Fixed', label: 'price, agreed before we start' },
];

const layers = [
  { name: 'Sources', className: 'layer-sources', text: 'Business systems and file extracts in cloud storage (10 tables)' },
  { name: 'Bronze', className: 'layer-bronze', text: 'Raw data as landed, with load metadata on every row' },
  { name: 'Silver', className: 'layer-silver', text: 'Cleaned, typed and historized so changes are kept' },
  { name: 'Gold', className: 'layer-gold', text: 'Engineering tables behind consumer views' },
  { name: 'Reporting', className: 'layer-reporting', text: 'The one agreed reporting outcome for your users' },
];

const platformPoints = [
  { title: 'Unity Catalog', text: 'Groups own every object, tags classify data, no persistent data on DBFS' },
  { title: 'Lakeflow Jobs', text: 'Scheduled runs with task dependencies, retries and alerts' },
  { title: 'Serverless compute', text: 'Used by default, with a region and cost plan agreed up front' },
];

const phases = [
  {
    number: 1,
    weeks: 'Weeks 1-2',
    title: 'Discovery & Scoping',
    items: ['One pipeline, 10 tables, done criteria', 'Profile users, workloads and sources', 'Compliance, ingestion and history choices'],
    gate: 'Scoping document signed',
  },
  {
    number: 2,
    weeks: 'Weeks 3-4',
    title: 'Foundation & Kickoff',
    items: ['Region and compute plan', 'Keys and time rules for every table', 'Owners, tags and storage guardrails'],
    gate: 'Foundation readiness record',
  },
  {
    number: 3,
    weeks: 'Weeks 5-6',
    title: 'Land & Solidify Bronze',
    items: ['Initial snapshot, then incremental loads', 'Load metadata on every row', 'Validate counts, keys, time ranges'],
    gate: 'Bronze acceptance checklist',
  },
  {
    number: 4,
    weeks: 'Weeks 7-8',
    title: 'Land & Solidify Silver',
    items: ['Column mappings and type conversions', 'Type 2 history for changing records', 'Layout tuned, proven by scan metrics'],
    gate: 'Silver validated',
  },
  {
    number: 5,
    weeks: 'Weeks 9-10',
    title: 'Develop Gold Layer',
    items: ['Consumer views and your reporting outcome', 'Quality rules with alerting', 'Role-based access, sensitive data masked'],
    gate: 'Gold accepted',
  },
  {
    number: 6,
    weeks: 'Weeks 11-13',
    title: 'Optimize, Govern & Close',
    items: ['Load test, tune and set warehouse sizes', 'Lakeflow Jobs and bundle deployment', 'Runbook, guides, cost summary'],
    gate: 'Customer sign-off against done criteria',
  },
];

const included = [
  'One medallion pipeline: 10 source tables, no more than 10 per layer',
  'Signed scoping document and done criteria',
  'Unity Catalog setup: owners, tags, access',
  'Bronze and Silver with validation and history',
  'Gold views and one reporting outcome',
  'Lakeflow Jobs, runbook, guides, handover',
];

const parked = [
  'Extra tables or additional source systems',
  'Further reports or dashboards',
  'Fixing source data quality, or consolidating small extract files upstream',
  'Ongoing managed support after handover',
  'Anything not in the signed scope',
];

const deliverables = [
  { title: 'Signed scoping document', text: 'Boundaries, SLAs and done criteria agreed up front' },
  { title: 'Working pipeline', text: 'Bronze, Silver and Gold running on an agreed schedule' },
  { title: 'Governed Gold views', text: 'Users see views only, with sensitive data masked' },
  { title: 'Tested and proven', text: 'Checks and load tests run against the done criteria' },
  { title: 'Operations runbook', text: 'Failure playbooks, retries, recovery steps and SLAs' },
  { title: 'Guides and cost summary', text: 'A guide per user type and a cost optimization summary' },
];

const commercialTerms = [
  'One fixed fee for the signed scope',
  'Invoiced at phase exit gates',
  'Changes priced and agreed in writing before work starts',
  'Follow-on options come from the backlog: more tables, more reports, managed support',
];

const clientAsks = [
  'Sources confirmed and reachable before kickoff',
  'Named users and outputs for the reporting outcome',
  'Your cloud region and storage access agreed in week 3',
  'Gate sign-off within agreed turnaround times',
];

const nextSteps = [
  { title: 'Discovery call', text: 'Your reporting need, your systems, your timeline' },
  { title: 'Scope the 10 tables', text: 'Agree the tables and the one reporting outcome' },
  { title: 'Fixed-price proposal', text: 'Written scope, plan, gates and price' },
];

const LakehousesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="lakehouses-page">
      {/* Hero Section */}
      <section className="lakehouse-hero">
        <div className="container">
          <p className="lakehouse-eyebrow">Fixed price · Fixed scope · 90 days</p>
          <h1>Your first lakehouse, planned and delivered in 90 days</h1>
          <p>
            A packaged delivery for medium-sized businesses that need trusted reporting and
            analysis on the Databricks Data Intelligence Platform
          </p>
          <Link to="/contact" className="cta-button">Book a scoping conversation</Link>
        </div>
      </section>

      {/* The Problem */}
      <section className="lakehouse-section">
        <div className="container">
          <h2>Reporting has outgrown spreadsheets and one-off extracts</h2>
          <div className="numbered-grid">
            {problems.map((problem, index) => (
              <div className="numbered-card" key={problem.title}>
                <span className="card-number">{index + 1}</span>
                <h3>{problem.title}</h3>
                <p>{problem.text}</p>
              </div>
            ))}
          </div>
          <p className="section-callout">
            A lakehouse gives one governed copy of your data, built once and reused for every report.
          </p>
        </div>
      </section>

      {/* The Offer */}
      <section className="lakehouse-section alt">
        <div className="container">
          <h2>The offer: a clearly bounded first lakehouse</h2>
          <div className="stats-grid">
            {stats.map((stat) => (
              <div className="stat" key={stat.label}>
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
          <p className="section-intro">
            Six phases, each with an exit gate: we agree what "done" means in writing before any
            build starts, then prove it at the end. Scope is one pipeline with no more than 10
            tables per layer.
          </p>
        </div>
      </section>

      {/* Medallion Design */}
      <section className="lakehouse-section">
        <div className="container">
          <h2>From raw data to reports: the medallion design</h2>
          <div className="medallion-flow">
            {layers.map((layer) => (
              <div className={`medallion-layer ${layer.className}`} key={layer.name}>
                <h3>{layer.name}</h3>
                <p>{layer.text}</p>
              </div>
            ))}
          </div>
          <div className="platform-grid">
            {platformPoints.map((point) => (
              <div className="platform-point" key={point.title}>
                <h3>{point.title}</h3>
                <p>{point.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="lakehouse-section alt">
        <div className="container">
          <h2>Six phases, each with an exit gate</h2>
          <div className="phases-grid">
            {phases.map((phase) => (
              <div className="phase-card" key={phase.number}>
                <p className="phase-meta">Phase {phase.number} · {phase.weeks}</p>
                <h3>{phase.title}</h3>
                <ul>
                  {phase.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="phase-gate"><strong>Exit gate:</strong> {phase.gate}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scope */}
      <section className="lakehouse-section">
        <div className="container">
          <h2>What is in the fixed scope, and what is not</h2>
          <div className="two-column">
            <div className="scope-card included">
              <h3>Included in the fixed price</h3>
              <ul>
                {included.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="scope-card parked">
              <h3>Parked in a backlog for a follow-on</h3>
              <ul>
                {parked.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="lakehouse-section alt">
        <div className="container">
          <h2>What you have on day 90</h2>
          <div className="numbered-grid">
            {deliverables.map((item, index) => (
              <div className="numbered-card" key={item.title}>
                <span className="card-number">{index + 1}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commercials */}
      <section className="lakehouse-section">
        <div className="container">
          <h2>How a fixed price stays fixed</h2>
          <div className="two-column">
            <div className="scope-card">
              <h3>Commercial terms</h3>
              <ul>
                {commercialTerms.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="scope-card">
              <h3>What we ask of you</h3>
              <ul>
                {clientAsks.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="lakehouse-cta">
        <div className="container">
          <h2>Next step: a scoping conversation</h2>
          <div className="numbered-grid">
            {nextSteps.map((step, index) => (
              <div className="numbered-card" key={step.title}>
                <span className="card-number">{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
          <Link to="/contact" className="cta-button">Get in touch</Link>
          <p className="lakehouse-disclaimer">
            Approach based on the Databricks Partner Delivery
            Expert guidance. Databricks, Lakeflow and Unity Catalog are trademarks of Databricks, Inc.
          </p>
        </div>
      </section>
    </div>
  );
};

export default LakehousesPage;
